import { useEffect, useState } from "react";
import { ChevronLeft, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import client, { extractError } from "@/api";

const STATUS_LABEL = {
  OPEN: "Open", UNDER_REVIEW: "Under review",
  AWAITING_BUYER: "Awaiting buyer", AWAITING_SELLER: "Awaiting seller",
  RESOLVED_BUYER: "Resolved (buyer)", RESOLVED_SELLER: "Resolved (seller)",
  RESOLVED_SPLIT: "Resolved (split)", REJECTED: "Rejected", CLOSED: "Closed",
};
const FILTERS = [
  { value: "OPEN", label: "Open" },
  { value: "UNDER_REVIEW", label: "Under review" },
  { value: "AWAITING_BUYER", label: "Awaiting buyer" },
  { value: "AWAITING_SELLER", label: "Awaiting seller" },
  { value: "RESOLVED_BUYER", label: "Resolved (buyer)" },
  { value: "RESOLVED_SELLER", label: "Resolved (seller)" },
  { value: "CLOSED", label: "Closed" },
  { value: "", label: "All" },
];

function ResolveDialog({ onCancel, onConfirm, busy }) {
  const [resolution, setResolution] = useState("RESOLVED_BUYER");
  const [notes, setNotes] = useState("");
  const [internal, setInternal] = useState("");
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="resolve-dispute-dialog">
      <form className="dialog-panel" onSubmit={(e) => { e.preventDefault(); onConfirm({ resolution, resolution_notes: notes, internal_notes: internal || null }); }}>
        <h3>Resolve dispute</h3>
        <label>Outcome<select value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="resolve-outcome">
          <option value="RESOLVED_BUYER">In favour of buyer</option>
          <option value="RESOLVED_SELLER">In favour of seller</option>
          <option value="RESOLVED_SPLIT">Split resolution</option>
        </select></label>
        <label>Resolution notes (visible to parties, min 5 chars)<textarea required minLength={5} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="resolve-notes" /></label>
        <label>Internal notes (admin only)<textarea rows={2} value={internal} onChange={(e) => setInternal(e.target.value)} data-testid="resolve-internal" /></label>
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>Back</button>
          <button type="submit" className="primary-button" disabled={busy} data-testid="resolve-confirm">{busy ? "Working…" : "Confirm resolution"}</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDisputesPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("OPEN");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      params.set("page_size", 50);
      const r = await client.get(`/admin/disputes?${params.toString()}`);
      setItems(r.data.items || []); setCounts(r.data.counts || {});
    } catch (err) { setError(extractError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const open = async (id) => {
    try { const r = await client.get(`/admin/disputes/${id}`); setDetail(r.data); setMessage(""); setError(""); }
    catch (err) { setError(extractError(err)); }
  };
  const act = async (path, ok, payload = {}) => {
    if (!detail) return;
    setBusy(true); setError(""); setMessage("");
    try { const r = await client.post(`/admin/disputes/${detail.id}/${path}`, payload); setDetail((d) => ({ ...d, ...r.data })); setMessage(ok); setDialog(null); load(); }
    catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  if (detail) {
    return (
      <div className="admin-company-detail" data-testid="admin-dispute-detail">
        <button type="button" className="link-button back" onClick={() => setDetail(null)}><ChevronLeft size={14} /> Back to disputes</button>
        <header className="detail-header">
          <div>
            <p className="eyebrow">{STATUS_LABEL[detail.status]}</p>
            <h2>{detail.dispute_type.replace(/_/g, " ")}</h2>
            <p className="muted">Raised by {detail.raised_by_name} · Counterparty {detail.counterparty_name}</p>
          </div>
          <div className="chip-stack"><span className={`status-chip status-${detail.status.toLowerCase()}`}>{STATUS_LABEL[detail.status]}</span></div>
        </header>
        {error && <div className="form-error">{error}</div>}
        {message && <div className="form-success" data-testid="admin-dispute-message">{message}</div>}
        <section className="section-block">
          <p className="eyebrow">DESCRIPTION</p>
          <p>{detail.description}</p>
          <p className="muted small">Amount in dispute: ₹{detail.amount_in_dispute.toLocaleString("en-IN")}</p>
        </section>
        {detail.transaction && (
          <section className="detail-grid">
            <article><p className="eyebrow">TRANSACTION</p><p>{detail.transaction.listing_title}</p></article>
            <article><p className="eyebrow">TRADE VALUE</p><p>₹{detail.transaction.trade_value.toLocaleString("en-IN")}</p></article>
            <article><p className="eyebrow">SETTLEMENT</p><p>{detail.transaction.settlement_status}</p></article>
            <article><p className="eyebrow">FACILITATOR FEE</p><p>₹{detail.transaction.facilitator_fee_amount.toLocaleString("en-IN")}</p></article>
          </section>
        )}
        {detail.resolution_notes && (
          <section className="section-block">
            <p className="eyebrow">RESOLUTION</p>
            <p><strong>{detail.resolution}</strong> — {detail.resolution_notes}</p>
            {detail.internal_notes && <p className="muted small">Internal: {detail.internal_notes}</p>}
          </section>
        )}
        <section className="section-block">
          <p className="eyebrow">ACTIONS</p>
          <div className="button-row">
            {["OPEN", "AWAITING_BUYER", "AWAITING_SELLER"].includes(detail.status) &&
              <button type="button" className="ghost-button" onClick={() => act("review", "Moved to review.")} disabled={busy} data-testid="review-btn"><MessageSquare size={13} /> Start review</button>}
            {["OPEN", "UNDER_REVIEW"].includes(detail.status) && (
              <>
                <button type="button" className="ghost-button" onClick={() => act("request-information", "Buyer info requested.", { from_party: "BUYER", notes: "Please share evidence." })} disabled={busy} data-testid="request-buyer-info">Request buyer info</button>
                <button type="button" className="ghost-button" onClick={() => act("request-information", "Seller info requested.", { from_party: "SELLER", notes: "Please share evidence." })} disabled={busy} data-testid="request-seller-info">Request seller info</button>
              </>
            )}
            {!["CLOSED", "REJECTED", "RESOLVED_BUYER", "RESOLVED_SELLER", "RESOLVED_SPLIT"].includes(detail.status) && (
              <>
                <button type="button" className="primary-button" onClick={() => setDialog("resolve")} disabled={busy} data-testid="resolve-btn"><CheckCircle2 size={13} /> Resolve</button>
                <button type="button" className="danger-button-solid" onClick={() => act("reject", "Dispute rejected.", { reason: "Insufficient evidence or grounds." })} disabled={busy} data-testid="reject-btn"><XCircle size={13} /> Reject</button>
              </>
            )}
            {detail.status !== "CLOSED" &&
              <button type="button" className="ghost-button" onClick={() => act("close", "Dispute closed.")} disabled={busy} data-testid="close-btn">Close case</button>}
          </div>
        </section>
        {detail.evidence?.length > 0 && (
          <section className="documents-section">
            <div className="section-heading"><div><p className="eyebrow">EVIDENCE</p><h3>Uploaded documents</h3></div></div>
            <table className="admin-table"><thead><tr><th>Type</th><th>File</th><th>Size</th><th>By</th><th>When</th></tr></thead>
              <tbody>{detail.evidence.map((e) => (
                <tr key={e.id}><td>{e.document_type}</td><td><small>{e.filename}</small></td>
                  <td><small>{Math.round(e.size_bytes / 1024)} KB</small></td>
                  <td><small>{e.uploaded_by_company_id.slice(0, 8)}</small></td>
                  <td><small>{new Date(e.created_at).toLocaleString()}</small></td></tr>
              ))}</tbody></table>
          </section>
        )}
        {detail.audit_log?.length > 0 && (
          <section className="documents-section">
            <div className="section-heading"><div><p className="eyebrow">HISTORY</p><h3>Full audit trail</h3></div></div>
            <ul className="audit-list" data-testid="admin-dispute-audit">
              {detail.audit_log.map((a) => (
                <li key={a.id}>
                  <strong>{a.event}</strong>{a.is_internal ? <span className="badge">internal</span> : null}<small>{a.previous_state || "—"} → {a.new_state || "—"}</small>
                  {a.notes && <p className="muted small">{a.notes}</p>}
                  <small className="muted">{a.actor_email || "system"} · {a.actor_role} · {new Date(a.created_at).toLocaleString()}</small>
                </li>
              ))}
            </ul>
          </section>
        )}
        {dialog === "resolve" && <ResolveDialog onCancel={() => setDialog(null)} onConfirm={(p) => act("resolve", "Resolution recorded.", p)} busy={busy} />}
      </div>
    );
  }

  return (
    <div className="admin-listings-page" data-testid="admin-disputes-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="admin-dispute-tabs">
          {FILTERS.map((f) => (
            <button key={f.value || "all"} type="button" className={filter === f.value ? "active" : ""}
              data-testid={`admin-dispute-filter-${f.value || "all"}`} onClick={() => setFilter(f.value)}>
              {f.label}{f.value && counts[f.value] > 0 && <span className="count">{counts[f.value]}</span>}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="admin-disputes-table">
          <thead><tr><th>Type</th><th>Raised by</th><th>Counterparty</th><th>Amount</th><th>Status</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="empty-row">No disputes in this view.</td></tr>}
            {items.map((d) => (
              <tr key={d.id} data-testid={`admin-dispute-row-${d.id}`}>
                <td><strong>{d.dispute_type.replace(/_/g, " ")}</strong><br /><small className="muted">{d.transaction_id.slice(0, 8)}</small></td>
                <td>{d.raised_by_name}</td>
                <td>{d.counterparty_name}</td>
                <td>₹{d.amount_in_dispute.toLocaleString("en-IN")}</td>
                <td><span className={`status-chip status-${d.status.toLowerCase()}`}>{STATUS_LABEL[d.status]}</span></td>
                <td><small>{new Date(d.created_at).toLocaleDateString()}</small></td>
                <td><button type="button" className="link-button" data-testid={`admin-open-dispute-${d.id}`} onClick={() => open(d.id)}>Review →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
